import React, { useState } from "react";
import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { formatTime } from "@utils/timeGrid";
import { useAtelierStore, interventionSiblings, isMirroredJob } from "@store/atelierStore";
import type { WorkshopJob } from "../../types/garage/atelier";

const QUARTER = 0.25;

/**
 * Retrait d'un créneau quand l'intervention a d'autres morceaux posés : comme MyO,
 * « planifier un autre jour » (ce créneau seulement) ou « replanifier
 * l'intervention » (tous ses morceaux repartent en une seule charge).
 */
export function JobRemovalModal() {
  const pending = useAtelierStore((s) => s.jobRemovalPending);
  const jobs = useAtelierStore((s) => s.jobs);
  const setPending = useAtelierStore((s) => s.setJobRemovalPending);
  const returnJobToCharge = useAtelierStore((s) => s.returnJobToCharge);
  const returnInterventionToCharge = useAtelierStore((s) => s.returnInterventionToCharge);

  const job = pending ? jobs.find((j) => j.id === pending.jobId) : undefined;
  if (!pending || !job) return null;

  const siblings = interventionSiblings(jobs, job);
  // Un créneau MyO se retire avec les autres morceaux MyO (écrit dans MyO) ; un
  // créneau Carvis avec les morceaux Carvis. L'autre famille reste en place.
  const mirrored = isMirroredJob(job);
  const movable = [job, ...siblings.filter((j) => isMirroredJob(j) === mirrored)];
  const otherLeft = siblings.filter((j) => isMirroredJob(j) !== mirrored).length;
  const totalHours = +movable.reduce((sum, j) => sum + j.durationHours, 0).toFixed(2);
  const close = () => setPending(null);

  return (
    <BaseModal title="Retirer du planning" onClose={close} width="max-w-md">
      <div className="flex flex-col gap-4 p-4">
        <div className="text-h3" style={{ fontFamily: "Poppins", color: "#525252" }}>
          {job.clientName} a {movable.length} créneaux posés pour cette intervention.
        </div>
        <button
          className="flex flex-col items-start gap-1 p-3 rounded-lg text-left hover:bg-neutral-50 transition-colors"
          style={{ border: "1px solid #E5E5E5" }}
          onClick={() => {
            returnJobToCharge(job.id);
            close();
          }}
        >
          <span className="text-h3 font-medium" style={{ color: "#171717" }}>Retirer seulement ce créneau</span>
          <span className="text-h4" style={{ color: "#737373" }}>
            {job.date.split("-").reverse().join("/")} · {formatTime(job.startHour)} — {formatTime(job.startHour + job.durationHours)} ({job.durationHours}h) : à planifier un autre jour
          </span>
        </button>
        <button
          className="flex flex-col items-start gap-1 p-3 rounded-lg text-left hover:bg-indigo-50 transition-colors"
          style={{ border: "1px solid #C7D2FE", background: "#F5F6FF" }}
          onClick={() => {
            returnInterventionToCharge(job.id);
            close();
          }}
        >
          <span className="text-h3 font-medium" style={{ color: "#4338CA" }}>Retirer toute l'intervention</span>
          <span className="text-h4" style={{ color: "#6366F1" }}>
            Les {movable.length} créneaux ({totalHours}h) repartent en une seule charge à répartir, à replanifier
          </span>
        </button>
        {mirrored && (
          <div className="text-h4" style={{ color: "#6366F1" }}>
            Créneau venant de MyO : le retrait est écrit dans MyO (quelques secondes).
          </div>
        )}
        {otherLeft > 0 && (
          <div className="text-h4" style={{ color: "#A1A1A1" }}>
            {otherLeft} autre{otherLeft > 1 ? "s" : ""} créneau{otherLeft > 1 ? "x" : ""} {mirrored ? "posé" : "venant de MyO"}{otherLeft > 1 ? "s" : ""} reste{otherLeft > 1 ? "nt" : ""} en place.
          </div>
        )}
      </div>
      <div className="border-t p-4 flex flex-row justify-end">
        <SecondaryButton variant="small" onClick={close}>
          Annuler
        </SecondaryButton>
      </div>
    </BaseModal>
  );
}

/** Scinder un créneau posé : on garde une partie sur la grille, le reste repart « à répartir ». */
export function JobSplitModal() {
  const pending = useAtelierStore((s) => s.jobSplitPending);
  const jobs = useAtelierStore((s) => s.jobs);
  const setPending = useAtelierStore((s) => s.setJobSplitPending);
  const splitJob = useAtelierStore((s) => s.splitJob);

  const job = pending ? jobs.find((j) => j.id === pending.jobId) : undefined;
  const max = job ? +(job.durationHours - QUARTER).toFixed(2) : 0;
  const [keep, setKeep] = useState<number | null>(null);
  if (!pending || !job) return null;

  const keepHours = Math.min(max, Math.max(QUARTER, keep ?? Math.round((job.durationHours / 2) / QUARTER) * QUARTER));
  const remainder = +(job.durationHours - keepHours).toFixed(2);
  const close = () => {
    setKeep(null);
    setPending(null);
  };

  return (
    <BaseModal title="Scinder le créneau" onClose={close} width="max-w-md">
      <div className="flex flex-col gap-4 p-4">
        <div className="text-h3" style={{ fontFamily: "Poppins", color: "#525252" }}>
          {job.clientName} · {formatTime(job.startHour)} — {formatTime(job.startHour + job.durationHours)} ({job.durationHours}h)
        </div>
        <label className="flex flex-col gap-2">
          <span className="text-h4 font-medium" style={{ color: "#171717" }}>Heures gardées sur ce créneau</span>
          <div className="flex flex-row items-center gap-3">
            <input
              type="range"
              min={QUARTER}
              max={max}
              step={QUARTER}
              value={keepHours}
              onChange={(e) => setKeep(parseFloat(e.target.value))}
              style={{ flex: 1, accentColor: "#6366F1" }}
            />
            <span className="text-h3 font-medium" style={{ color: "#4338CA", minWidth: 48, textAlign: "right" }}>
              {keepHours}h
            </span>
          </div>
        </label>
        <div
          className="flex flex-col gap-1 p-3 rounded-lg"
          style={{ background: "#F0F4FF", border: "1px solid #C7D2FE" }}
        >
          <div className="text-h4" style={{ color: "#4338CA" }}>
            Sur la grille : {formatTime(job.startHour)} — {formatTime(job.startHour + keepHours)} ({keepHours}h)
          </div>
          <div className="text-h4" style={{ color: "#6366F1" }}>
            {isMirroredJob(job)
              ? `Second morceau : ${remainder}h, juste après sur la grille (comme dans MyO), à déplacer ensuite`
              : `À répartir : ${remainder}h, à glisser sur un autre créneau ou compagnon`}
          </div>
        </div>
      </div>
      <div className="border-t p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={close}>
          Annuler
        </SecondaryButton>
        <Button
          variant="small"
          label="Scinder"
          onClick={() => {
            splitJob(job.id, keepHours);
            close();
          }}
        />
      </div>
    </BaseModal>
  );
}

/** Actions du clic droit sur un créneau atelier (en tête du menu de statut).
 *  Sur un créneau MyO, elles sont écrites dans MyO (voir runMyo). */
export function jobMenuActions(job: WorkshopJob) {
  const hint = isMirroredJob(job) ? "Écrit dans MyO" : undefined;
  const { setJobSplitPending, requestJobRemoval } = useAtelierStore.getState();
  return [
    {
      label: "Scinder le créneau…",
      onClick: () => setJobSplitPending({ jobId: job.id }),
      disabled: job.durationHours <= QUARTER,
      hint: job.durationHours <= QUARTER ? "Créneau trop court pour être scindé" : hint,
    },
    {
      label: "Retirer du planning…",
      onClick: () => requestJobRemoval(job.id),
      hint,
    },
  ];
}
